"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NextIntlClientProvider } from "next-intl";
import itMessages from "../../messages/it.json";
import enMessages from "../../messages/en.json";
import { routing, type Locale } from "./routing";
import client from "@/lib/api";
import { useAuth } from "@/components/auth-provider";

const messagesByLocale = {
  it: itMessages,
  en: enMessages,
} as const;

const LOCALE_COOKIE = "ps-locale";

function readLocaleCookie(): Locale | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`),
  );
  const value = match?.[1];
  return routing.locales.includes(value as Locale) ? (value as Locale) : null;
}

function writeLocaleCookie(locale: Locale) {
  // 1 year, site-wide
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=31536000; samesite=lax`;
}

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const initialCookieLocale = useRef(readLocaleCookie()).current;
  const [locale, setLocaleState] = useState<Locale>(
    initialCookieLocale ?? routing.defaultLocale
  );

  const setLocale = useCallback((next: Locale) => {
    writeLocaleCookie(next);
    setLocaleState(next);
    if (typeof document !== "undefined") {
      document.documentElement.lang = next;
    }
    if (user) {
      void client
        .PATCH("/account", {
          body: { preferred_locale: { set: true, valid: true, value: next } },
        })
        .catch(() => undefined);
    }
  }, [user]);

  useEffect(() => {
    if (initialCookieLocale) return;
    const preferredLocale = user?.preferredLocale;
    if (!routing.locales.includes(preferredLocale as Locale)) return;
    const next = preferredLocale as Locale;
    writeLocaleCookie(next);
    setLocaleState(next);
    document.documentElement.lang = next;
  }, [initialCookieLocale, user?.preferredLocale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <LocaleContext.Provider value={value}>
      <NextIntlClientProvider
        locale={locale}
        messages={messagesByLocale[locale]}
        timeZone="Europe/Rome"
      >
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}

export function useSetLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useSetLocale must be used within LocaleProvider");
  return ctx;
}
